import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { Icon } from 'js/components';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import styles from './AssessmentReviewDetailBreakouts.module.scss';

export default function BreakoutListItem({
  breakout,
  question,
  index,
  to,
  onRequestDelete,
}) {
  const ref = useRef(null);
  const dragHandleRef = useRef(null);
  const [closestEdge, setClosestEdge] = useState(null);

  useEffect(() => {
    const el = ref.current;
    const dragHandle = dragHandleRef.current;
    if (!el || !dragHandle) {
      return undefined;
    }

    return combine(
      draggable({
        element: el,
        dragHandle,
        getInitialData: () => ({
          type: 'breakout-item',
          index,
          breakout,
        }),
        onGenerateDragPreview: ({ source }) => {
          source.element.classList.add('drag-preview');
        },
        onDragStart: ({ source }) => {
          source.element.classList.remove('drag-preview');
        },
        onDrop: ({ source }) => {
          source.element.classList.add('drag-preview');
          window.requestAnimationFrame(() => {
            source.element.classList.remove('drag-preview');
          });
        },
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) =>
          source.data?.type === 'breakout-item' && source.data.index !== index,
        getData: ({ input, element }) =>
          attachClosestEdge(
            { type: 'breakout-item', index, breakout },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          ),
        onDrag({ self, source }) {
          if (source.element === el) {
            setClosestEdge(null);
            return;
          }
          const edge = extractClosestEdge(self.data);
          const sourceIndex = source.data.index;
          const isItemBeforeSource = index === sourceIndex - 1;
          const isItemAfterSource = index === sourceIndex + 1;
          const hide =
            (isItemBeforeSource && edge === 'bottom') ||
            (isItemAfterSource && edge === 'top');
          setClosestEdge(hide ? null : edge);
        },
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null),
      })
    );
  }, [breakout, index]);

  return (
    <li ref={ref} className={styles.listItem}>
      {closestEdge != null && (
        <DropIndicator
          top={closestEdge === 'top'}
          bottom={closestEdge === 'bottom'}
        />
      )}
      <button
        type="button"
        className={`drag-handle ${styles.dragHandle}`}
        ref={dragHandleRef}
        aria-label="Drag to reorder breakout"
      >
        <Icon icon={faGripVertical} className="icon" />
      </button>
      <Link to={to} className={styles.itemMain}>
        <span className={styles.itemName}>{breakout.name || 'Untitled'}</span>
        <span className={styles.itemMeta}>
          {question?.text || 'No question selected'}
        </span>
      </Link>
      <Button
        type="button"
        className={`btn-action-alt ${styles.deleteBtn}`}
        color="secondary"
        onClick={onRequestDelete}
        aria-label={`Delete ${breakout.name || 'breakout'}`}
      >
        <Icon icon={faTrashAlt} className="icon" />
      </Button>
    </li>
  );
}
